import { useEffect, useMemo, useRef, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { ArrowsClockwise, MapPin, Truck, Wrench, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AdjustTimesButton } from "@/components/AdjustTimesButton";

/**
 * Live Field Tracker — map of every tech currently clocked-in.
 * URL: /admin/live · Auto-refreshes every 20s.
 * Uses `clock_in_location` GPS captured at clock-in.
 */

// Same visual language as MapView.jsx — teardrop pin, color = tech status.
const STATUS_COLOR = {
  on_the_way:   "#3b82f6",  // blue
  on_site:      "#14b8a6",  // teal
  material_run: "#f59e0b",  // amber
  idle:         "#94a3b8",  // slate
};
const STATUS_LABEL = {
  on_the_way:   "On the way",
  on_site:      "On site",
  material_run: "Material run",
  idle:         "Idle",
};

const makeIcon = (color, initials) => L.divIcon({
  className: "field-radar-tech-pin",
  html: `
    <div style="width:38px;height:52px;position:relative;filter:drop-shadow(0 3px 5px rgba(0,0,0,0.5));">
      <svg viewBox="0 0 38 52" width="38" height="52" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 1 C9.06 1 1 9.06 1 19 c0 12.5 18 32 18 32 s18 -19.5 18 -32 C37 9.06 28.94 1 19 1 z"
              fill="${color}" stroke="#0f172a" stroke-width="2"/>
        <circle cx="19" cy="19" r="10" fill="#ffffff"/>
      </svg>
      <div style="position:absolute;top:9px;left:0;right:0;text-align:center;font-family:system-ui;font-weight:800;font-size:11px;color:${color};line-height:20px;">${initials}</div>
    </div>`,
  iconSize: [38, 52],
  iconAnchor: [19, 50],
  popupAnchor: [0, -46],
});

const initials = (name) => (name || "?").split(/\s+/).slice(0, 2).map(s => s[0] || "").join("").toUpperCase() || "?";

export default function LiveField() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [mapOpen, setMapOpen] = useState(() => {
    const saved = localStorage.getItem("live_field_map_open");
    return saved === null ? true : saved === "1";
  });
  useEffect(() => {
    localStorage.setItem("live_field_map_open", mapOpen ? "1" : "0");
  }, [mapOpen]);
  const [loading, setLoading] = useState(true);
  const [moveModal, setMoveModal] = useState(null);  // { tech }
  const [forceClockIn, setForceClockIn] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/live-techs");
      setData(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load live techs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60000);   // 60s auto-refresh — dispatchers rarely need sub-minute pin position; keeps backend load light on tier_1.
    return () => clearInterval(timerRef.current);
  }, []);

  const withGps  = useMemo(() => (data?.techs || []).filter(t => t.lat != null && t.lng != null), [data]);
  const offMap   = useMemo(() => (data?.techs || []).filter(t => t.lat == null || t.lng == null), [data]);
  const center   = withGps.length
                    ? [withGps.reduce((s, t) => s + t.lat, 0) / withGps.length,
                       withGps.reduce((s, t) => s + t.lng, 0) / withGps.length]
                    : [39.9526, -75.1652];  // Philly default

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-3 sm:p-4 space-y-3" data-testid="live-field-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight flex items-center gap-2">
              <MapPin weight="fill" size={26} className="text-cyan-600"/> Live Field
            </h1>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {loading ? "Loading…" : `${data?.total || 0} clocked in · ${data?.with_gps || 0} on map · auto-refresh 20s`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setForceClockIn(true)} data-testid="live-force-clockin-open"
              className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-emerald-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <User weight="bold" size={13}/> Force Clock-In
            </button>
            <button onClick={load} data-testid="live-refresh"
              className="h-10 px-3 bg-white hover:bg-slate-50 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <ArrowsClockwise weight="bold" size={13}/> Refresh
            </button>
          </div>
        </div>

        {err && <div className="p-3 bg-red-50 border-2 border-red-500 text-red-800 text-sm font-semibold" data-testid="live-error">{err}</div>}

        {/* Over-shift banner: techs clocked in >10h — likely forgot to close. */}
        {(data?.over_shift || 0) > 0 && (
          <div className="p-3 bg-red-50 border-2 border-red-500" data-testid="live-over-shift">
            <div className="text-xs font-black uppercase tracking-widest text-red-800 mb-2">
              ⚠ {data.over_shift} tech{data.over_shift === 1 ? " has" : "s have"} been on the clock &gt; 10 hours
            </div>
            <div className="space-y-1.5">
              {(data.techs || []).filter(t => t.over_shift).map(t => (
                <div key={t.id} className="flex items-center gap-2 flex-wrap text-sm bg-white/60 p-2 rounded" data-testid={`live-stale-${t.id}`}>
                  <User weight="fill" size={14} className="text-red-700"/>
                  <span className="font-bold">{t.name}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-red-700">
                    {t.hours_on_clock}h on clock
                  </span>
                  {t.job?.id && (
                    <Link to={`/admin/jobs/${t.job.id}`} className="text-cyan-700 hover:underline text-xs">
                      · {t.job.address || t.job.title}
                    </Link>
                  )}
                  {t.job?.id && (
                    <AdjustTimesButton
                      job={{ id: t.job.id, work_order_number: t.job.number,
                             clock_in_at: t.clock_in_at || t.job.clock_in_at,
                             clock_out_at: null, en_route_at: t.job.en_route_at,
                             paused_at: t.job.paused_at,
                             lunch_start_at: t.job.lunch_start_at,
                             total_lunch_minutes: t.job.total_lunch_minutes }}
                      onSaved={() => load()}
                      size="sm"
                      label="Fix time"
                      testid={`live-adjust-times-${t.id}`}
                    />
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Clock ${t.name} out of "${t.job?.address || t.job?.title}"? They can re-clock in if they're still working.`)) return;
                      try {
                        const r = await api.post(`/live-techs/force-clockout/${t.job.id}`);
                        toast.success(r.data?.mode === "en_route_cancelled"
                          ? `✓ Cancelled ${t.name}'s en-route (they weren't actually clocked in)`
                          : `✓ Clocked ${t.name} out`);
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Could not clock out"); }
                    }}
                    data-testid={`live-force-clockout-${t.id}`}
                    className="ml-auto h-8 px-2 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest rounded">
                    Force Clock-Out
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status legend + map toggle */}
        <div className="flex flex-wrap gap-2 items-center text-[10px] font-black uppercase tracking-widest" data-testid="live-legend">
          {Object.entries(STATUS_LABEL).map(([k, label]) => (
            <div key={k} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-slate-300">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[k] }}></span>
              <span className="text-slate-700">{label}</span>
            </div>
          ))}
          <button onClick={() => setMapOpen(o => !o)}
            data-testid="live-map-toggle"
            className="ml-auto px-3 h-8 border-2 border-[#0f172a] bg-white hover:bg-slate-100 font-black uppercase tracking-widest text-[10px]">
            {mapOpen ? "✕ HIDE MAP" : "🗺️ SHOW MAP"}
          </button>
        </div>

        {/* Map */}
        {mapOpen && (
        <div className="bg-white border-2 border-[#0f172a] overflow-hidden relative" data-testid="live-map-wrap"
             style={{ height: "60vh", minHeight: 400 }}>
          <button onClick={() => setMapOpen(false)}
            data-testid="live-map-close"
            aria-label="Close map"
            className="absolute top-2 right-2 z-[500] w-10 h-10 border-2 border-[#0f172a] bg-white hover:bg-red-100 font-black text-xl leading-none shadow-[2px_2px_0_#0f172a]">
            ✕
          </button>
          <MapContainer center={center} zoom={withGps.length ? 12 : 10} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {withGps.map(t => (
              <Marker key={t.id} position={[t.lat, t.lng]}
                icon={makeIcon(STATUS_COLOR[t.status] || STATUS_COLOR.idle, initials(t.name))}>
                <Tooltip>{t.name} · {STATUS_LABEL[t.status] || t.status}</Tooltip>
                <Popup>
                  <div className="min-w-[200px] font-sans">
                    <div className="font-display font-black text-base" style={{ color: STATUS_COLOR[t.status] }}>
                      {t.name}
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                      {STATUS_LABEL[t.status] || t.status} · {t.role}
                      {t.loc_source === "live" && (
                        <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded" data-testid={`live-navigating-${t.id}`}>
                          🚗 Navigating
                        </span>
                      )}
                    </div>
                    {t.job?.id && (
                      <div className="text-xs text-slate-700 leading-snug">
                        <b>Job #{t.job.number || t.job.id.slice(-6)}</b><br/>
                        {t.job.address}{t.job.unit ? ` · Unit ${t.job.unit}` : ""}<br/>
                        <span className="text-slate-500">{t.job.title}</span>
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {t.job?.id && (
                        <Link to={`/admin/jobs/${t.job.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest">
                          Open Job
                        </Link>
                      )}
                      {t.phone && (
                        <a href={`tel:${t.phone}`}
                          className="inline-flex items-center gap-1 h-8 px-2 bg-cyan-700 text-white text-[10px] font-black uppercase tracking-widest">
                          Call
                        </a>
                      )}
                      {t.job?.id && (
                        <button onClick={() => setMoveModal({ tech: t })}
                          data-testid={`live-move-session-${t.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest">
                          Move to WO
                        </button>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        )}

        {/* Off-map list — session active (clocked in OR en-route) but
            GPS not captured. Includes en-route-only techs so the admin can
            cancel a stale "On my way" tap. */}
        {offMap.length > 0 && (
          <div className="bg-white border-2 border-amber-500 p-3" data-testid="live-off-map">
            <div className="text-xs font-black uppercase tracking-widest text-amber-700 mb-2">
              {offMap.length} active session{offMap.length === 1 ? "" : "s"} without GPS
            </div>
            <div className="space-y-1.5">
              {offMap.map(t => (
                <div key={t.id} className="flex items-center gap-2 text-sm flex-wrap">
                  <User weight="fill" size={14} className="text-slate-500"/>
                  <span className="font-semibold">{t.name}</span>
                  {t.gps_hint === "blocked" && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-red-100 text-red-800 border border-red-400 px-1 py-0.5 rounded"
                      title="Tech clocked in but phone never sent a GPS fix — likely a location-permission issue. Call them.">
                      📵 GPS blocked
                    </span>
                  )}
                  {t.gps_hint === "stale" && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-900 border border-amber-400 px-1 py-0.5 rounded"
                      title="Had GPS at clock-in, no new fix in >30 min — likely dead signal area or app backgrounded.">
                      📶 GPS stale
                    </span>
                  )}
                  {t.gps_hint === "en_route_only" && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-700 border border-slate-400 px-1 py-0.5 rounded"
                      title="Tapped 'On my way' but never clocked in. No GPS fix sent yet.">
                      🚗 En-route only
                    </span>
                  )}
                  {t.job?.id && (
                    <Link to={`/admin/jobs/${t.job.id}`} className="text-cyan-700 hover:underline text-xs">
                      · Job #{t.job.number} · {t.job.address}
                    </Link>
                  )}
                  {t.job?.id && (
                    <div className="ml-auto flex items-center gap-1.5">
                      <button onClick={() => setMoveModal({ tech: t })}
                        data-testid={`live-off-move-${t.id}`}
                        className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest rounded">
                        Move to WO
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Clock ${t.name} out of "${t.job?.address || t.job?.title}"? They can re-clock in if they're still working.`)) return;
                          try {
                            const r = await api.post(`/live-techs/force-clockout/${t.job.id}`);
                            toast.success(r.data?.mode === "en_route_cancelled"
                              ? `✓ Cancelled ${t.name}'s en-route (they weren't actually clocked in)`
                              : `✓ Clocked ${t.name} out`);
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Could not clock out"); }
                        }}
                        data-testid={`live-off-force-clockout-${t.id}`}
                        className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest rounded">
                        Clock Out
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tech list — same info, tap-friendly on mobile */}
        {withGps.length > 0 && (
          <div className="bg-white border-2 border-slate-300 p-3" data-testid="live-tech-list">
            <div className="text-xs font-black uppercase tracking-widest text-slate-700 mb-2">On the map</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {withGps.map(t => (
                <div key={t.id} data-testid={`live-tech-${t.id}`}
                  className="p-2.5 border border-slate-200 hover:border-[#0f172a] transition flex items-center gap-2">
                  <Link to={t.job?.id ? `/admin/jobs/${t.job.id}` : "#"}
                    className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full text-white font-black text-xs"
                      style={{ backgroundColor: STATUS_COLOR[t.status] || STATUS_COLOR.idle }}>
                      {initials(t.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{t.name}</div>
                      <div className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1" style={{ color: STATUS_COLOR[t.status] }}>
                        {STATUS_LABEL[t.status] || t.status}
                        {t.loc_source === "live" && <span className="text-blue-700">· 🚗 navigating</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {t.job?.address ? `${t.job.address}${t.job.unit ? " · " + t.job.unit : ""}` : "no active job"}
                      </div>
                    </div>
                  </Link>
                  {t.job?.id && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.preventDefault(); setMoveModal({ tech: t }); }}
                        data-testid={`live-tile-move-${t.id}`}
                        title="Move session to a different WO"
                        className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest rounded">
                        Move
                      </button>
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          if (!window.confirm(`Clock ${t.name} out of "${t.job?.address || t.job?.title}"? They can re-clock in if they're still working.`)) return;
                          try {
                            const r = await api.post(`/live-techs/force-clockout/${t.job.id}`);
                            toast.success(r.data?.mode === "en_route_cancelled"
                              ? `✓ Cancelled ${t.name}'s en-route (they weren't actually clocked in)`
                              : `✓ Clocked ${t.name} out`);
                            load();
                          } catch (err) { toast.error(err?.response?.data?.detail || "Could not clock out"); }
                        }}
                        data-testid={`live-tile-force-clockout-${t.id}`}
                        title="Force clock-out (they can re-clock in)"
                        className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest rounded">
                        Clock Out
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && (data?.total || 0) === 0 && (
          <div className="p-8 text-center bg-white border-2 border-slate-300" data-testid="live-empty">
            <Wrench weight="fill" size={32} className="mx-auto text-slate-400 mb-2"/>
            <div className="font-display font-black text-lg text-slate-700">Nobody in the field right now</div>
            <div className="text-xs text-slate-500 mt-1">Techs appear here the moment they tap "On my way" or clock into a job.</div>
          </div>
        )}
      </div>
      {moveModal && (
        <MoveSessionModal
          tech={moveModal.tech}
          onClose={() => setMoveModal(null)}
          onMoved={() => { setMoveModal(null); load(); }}
        />
      )}
      {forceClockIn && (
        <ForceClockInModal
          onClose={() => setForceClockIn(false)}
          onDone={() => { setForceClockIn(false); load(); }}
        />
      )}
    </AdminShell>
  );
}

// Small in-page modal — dispatcher picks the correct WO to receive the
// tech's active session (en-route or clocked-in). Searches jobs live via
// /api/jobs?q= so any 3-5 digit WO number or address prefix matches.
function MoveSessionModal({ tech, onClose, onMoved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/jobs`, { params: { q: s, limit: 15 } });
        setResults((r.data || []).filter(j => j.id !== tech.job?.id));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, tech.job?.id]);
  const doMove = async (jobId) => {
    if (!window.confirm(`Move ${tech.name}'s active session to this WO?\n\nThe clock/en-route stamps and GPS transfer over — the tech doesn't need to redo anything.`)) return;
    setBusy(true);
    try {
      await api.post(`/live-techs/move-session/${tech.job.id}`, { to_job_id: jobId, reason });
      toast.success("✓ Session moved");
      onMoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[300] bg-black/80 flex items-center justify-center p-4" data-testid="move-session-modal">
      <div className="bg-white w-full max-w-lg border-2 border-[#0f172a] shadow-2xl">
        <div className="p-3 border-b-2 border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Move session</div>
            <div className="font-display font-black text-lg">{tech.name}</div>
            <div className="text-xs text-slate-600">Currently on WO #{tech.job?.number || tech.job?.id?.slice(-6)} · {tech.job?.address}</div>
          </div>
          <button onClick={onClose} data-testid="move-session-close"
            className="h-8 w-8 flex items-center justify-center hover:bg-slate-100">✕</button>
        </div>
        <div className="p-3 space-y-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="move-session-search"
            placeholder="Search by WO # or address…"
            className="w-full h-10 px-3 border-2 border-slate-300 focus:border-[#00C4CC] outline-none text-sm"/>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="move-session-reason"
            placeholder="Reason (optional — e.g. 'clocked in wrong WO')"
            className="w-full h-9 px-3 border border-slate-300 outline-none text-xs text-slate-700"/>
          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded" data-testid="move-session-results">
            {q.trim().length < 2 && (
              <div className="p-4 text-center text-xs text-slate-400">Type 2+ characters to search</div>
            )}
            {q.trim().length >= 2 && results.length === 0 && (
              <div className="p-4 text-center text-xs text-slate-400">No matching WOs</div>
            )}
            {results.map(j => (
              <button key={j.id} onClick={() => doMove(j.id)} disabled={busy}
                data-testid={`move-session-pick-${j.id}`}
                className="w-full text-left p-2.5 border-b border-slate-100 hover:bg-amber-50 disabled:opacity-60 transition">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center h-5 px-1.5 bg-[#00C4CC] text-white text-[10px] font-black font-mono rounded">
                    WO{j.work_order_number || j.id.slice(-4)}
                  </span>
                  <span className="text-sm font-semibold truncate">{j.property_address || j.title}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                  {j.unit ? `Unit ${j.unit} · ` : ""}{j.status}{j.assigned_to_name ? ` · Assigned ${j.assigned_to_name}` : ""}
                </div>
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 leading-relaxed">
            Moving carries the tech's en-route stamp, clock-in, GPS, and ETA to the new WO. The old WO's status flips back to <b>Assigned</b> and both jobs get an audit log entry.
          </div>
        </div>
      </div>
    </div>
  );
}

// Force Clock-In — coordinator/admin picks a worker + WO and stamps
// clock_in_at. Backend auto-clocks-out any other active session for the
// same worker so the timer stays clean. Powers the "Alejandro is on-site
// but forgot to clock in" recovery flow.
function ForceClockInModal({ onClose, onDone }) {
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState("");
  const [q, setQ] = useState("");
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [note, setNote] = useState("");
  const [when, setWhen] = useState("");   // optional custom clock-in time
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Assignable users = workers + admins, so a coordinator can force-clock-in
    // a fellow admin too (rare but possible).
    api.get("/assignable-users").then(r => {
      const list = (r.data || []).map(u => ({
        id: u.id || u._id,
        name: u.name || u.email,
        role: u.role || "worker",
      }));
      setWorkers(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setJobs([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/jobs", { params: { q: s, limit: 15 } });
        setJobs((r.data || []).filter(j => !["completed", "canceled"].includes(j.status)));
      } catch { setJobs([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    if (!workerId || !jobId) {
      toast.error("Pick a worker and a WO first"); return;
    }
    setBusy(true);
    try {
      const payload = { worker_id: workerId, job_id: jobId };
      if (when) {
        const d = new Date(when);
        if (!isNaN(d.getTime())) payload.when = d.toISOString();
      }
      if (note.trim()) payload.note = note.trim().slice(0, 500);
      const r = await api.post("/live-techs/force-clockin", payload);
      let msg = `✓ Clocked ${r.data.worker_name || "worker"} in`;
      if (r.data.also_clocked_out_job_id) msg += " (auto-clocked-out of the other WO)";
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force clock-in failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-3" onClick={onClose}
      data-testid="force-clockin-modal">
      <div className="bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] max-w-xl w-full max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="font-black uppercase tracking-widest text-xs text-emerald-700">Force Clock-In</div>
            <div className="text-[10px] text-slate-500">Records the tech on-site — auto-clocks-out any active session on another WO.</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center text-slate-500 hover:text-slate-900" data-testid="force-clockin-close">✕</button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Worker</div>
            <select value={workerId} onChange={e => setWorkerId(e.target.value)}
              data-testid="force-clockin-worker"
              className="w-full h-11 px-3 border-2 border-[#0f172a] bg-white text-sm font-semibold">
              <option value="">— Pick a worker —</option>
              {workers.map(w => (
                <option key={w.id} value={w.id}>
                  {w.name}{w.role === "worker" ? "" : ` · ${w.role || "admin"}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Work Order</div>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search WO# or address…"
              data-testid="force-clockin-wo-search"
              className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            {jobs.length > 0 && (
              <div className="mt-1.5 max-h-52 overflow-y-auto border border-slate-200 rounded">
                {jobs.map(j => (
                  <button key={j.id} onClick={() => { setJobId(j.id); setQ(`WO ${j.work_order_number || ""} · ${j.property_address || j.title}`); setJobs([]); }}
                    data-testid={`force-clockin-wo-${j.id}`}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${jobId === j.id ? "bg-emerald-50" : ""}`}>
                    <div className="text-[11px] font-black text-emerald-700">WO {j.work_order_number || j.id.slice(-6)}</div>
                    <div className="text-sm font-semibold truncate">{j.property_address || j.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">{j.unit ? `Unit ${j.unit} · ` : ""}{j.status}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Clock-In Time (optional)</div>
              <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
                data-testid="force-clockin-when"
                className="w-full h-11 px-2 border-2 border-[#0f172a] text-sm"/>
              <div className="text-[9px] text-slate-500 mt-0.5">Leave blank to stamp now</div>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Note (optional)</div>
              <input value={note} onChange={e => setNote(e.target.value)} maxLength={500}
                placeholder="Was on-site by 8:30 per Grace"
                data-testid="force-clockin-note"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-between gap-2 flex-shrink-0 bg-slate-50">
          <button onClick={onClose} className="h-10 px-3 border-2 border-slate-300 text-slate-700 font-black uppercase text-[10px] tracking-widest">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !workerId || !jobId}
            data-testid="force-clockin-submit"
            className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            {busy ? "Clocking in…" : "Clock In Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

